import type { StorageAdapter } from "./StorageAdapter.ts";
import type { MetaState } from "../core/MetaState.ts";
import {
  getDefaultMetaState,
  normalizeMetaState,
  DEFAULT_SFX_VOLUME,
  DEFAULT_MUSIC_VOLUME,
  DEFAULT_THREAT_LEVEL,
} from "../core/MetaState.ts";
import { DEFAULT_INITIALS } from "../core/initials.ts";
import { L_UI_SCALE } from "../utils/uiScale.ts";

const META_STORAGE_KEY = "ai-wars-meta";
// D-352 (2026-threat-profile-model) bumped it 6 → 7: `settings.difficulty`
// → `settings.threatLevel` (migration registered at key 6, below).
export const CURRENT_SCHEMA_VERSION = 8;

type Migration = (state: Record<string, unknown>) => Record<string, unknown>;

export type LoadResult = {
  state: MetaState;
  /**
   * "clean" = loaded normally, "migrated" = version upgraded,
   * "recovered" = current-version blob had missing/garbage fields that were
   * repaired from defaults (BL-86), "reset" = corrupted/unreadable.
   */
  status: "clean" | "migrated" | "recovered" | "reset";
};

/**
 * Manages MetaState persistence via StorageAdapter.
 * Handles schema migrations for forward-compatibility.
 */
export class MetaStateManager {
  private adapter: StorageAdapter;
  private migrations: Map<number, Migration> = new Map();

  constructor(adapter: StorageAdapter) {
    this.adapter = adapter;
    this.registerMigrations();
  }

  async load(): Promise<LoadResult> {
    const raw = await this.adapter.load(META_STORAGE_KEY);
    if (!raw) return { state: getDefaultMetaState(), status: "clean" };

    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      console.warn(
        "[MetaStateManager] Corrupt save data — resetting to defaults",
      );
      return { state: getDefaultMetaState(), status: "reset" };
    }

    const version = (parsed.schemaVersion as number) ?? 0;

    // Unversioned (legacy v0) blob — cannot migrate, reset
    if (version < 1) {
      console.warn(
        "[MetaStateManager] Unversioned save (v0) — resetting to defaults",
      );
      return { state: getDefaultMetaState(), status: "reset" };
    }

    // Save is from a newer client — don't corrupt it, reset
    if (version > CURRENT_SCHEMA_VERSION) {
      console.warn(
        `[MetaStateManager] Save is newer (v${version}) than client (v${CURRENT_SCHEMA_VERSION}) — using defaults`,
      );
      return { state: getDefaultMetaState(), status: "reset" };
    }

    // Current version — no migration needed, but the blob is only known to be
    // the right *version*; its nested fields are untrusted. BL-86: normalize
    // over defaults so any missing/garbage field is repaired (never a boot
    // TypeError). Idempotent on a complete save (status stays "clean").
    if (version === CURRENT_SCHEMA_VERSION) {
      const { state, recovered } = normalizeMetaState(parsed);
      if (recovered) {
        console.warn(
          "[MetaStateManager] Save had missing/invalid fields — recovered from defaults",
        );
      }
      return { state, status: recovered ? "recovered" : "clean" };
    }

    // Migrate from older version, then normalize so a partial v1/v2 blob is
    // equally safe to consume post-migrate (status stays "migrated").
    const migrated = this.applyMigrations(parsed, version);
    const { state } = normalizeMetaState(migrated);
    return { state, status: "migrated" };
  }

  async save(state: MetaState): Promise<void> {
    const clone = structuredClone(state);
    clone.schemaVersion = CURRENT_SCHEMA_VERSION;
    const serialized = JSON.stringify(clone);
    await this.adapter.save(META_STORAGE_KEY, serialized);
  }

  async reset(): Promise<void> {
    await this.adapter.remove(META_STORAGE_KEY);
  }

  getDefault(): MetaState {
    return getDefaultMetaState();
  }

  private applyMigrations(
    state: Record<string, unknown>,
    fromVersion: number,
  ): Record<string, unknown> {
    let current = state;
    for (let v = fromVersion; v < CURRENT_SCHEMA_VERSION; v++) {
      const migration = this.migrations.get(v);
      if (migration) {
        current = migration(current);
      }
    }
    current.schemaVersion = CURRENT_SCHEMA_VERSION;
    return current;
  }

  private registerMigrations(): void {
    this.migrations.set(1, (state) => {
      const hs = state.highscores as Array<Record<string, unknown>> | undefined;
      if (Array.isArray(hs)) {
        for (const entry of hs) {
          if (!entry.initials) {
            entry.initials = DEFAULT_INITIALS;
          }
        }
      }
      if (!state.playerInitials) {
        state.playerInitials = DEFAULT_INITIALS;
      }
      return state;
    });

    // v2 → v3 (PT-19): split the single `audioMuted` into independent SFX/music
    // buses + volumes. A muted save mutes both buses; volumes are backfilled.
    this.migrations.set(2, (state) => {
      const settings = (state.settings ??= {}) as Record<string, unknown>;
      const wasMuted = settings.audioMuted === true;
      settings.sfxMuted = wasMuted;
      settings.musicMuted = wasMuted;
      if (typeof settings.sfxVolume !== "number") {
        settings.sfxVolume = DEFAULT_SFX_VOLUME;
      }
      if (typeof settings.musicVolume !== "number") {
        settings.musicVolume = DEFAULT_MUSIC_VOLUME;
      }
      delete settings.audioMuted;
      return state;
    });

    // v3 → v4 (BL-109): add the persisted text-size multiplier (`settings.uiScale`,
    // the `--ui-scale` knob). Migrating rather than letting `normalizeMetaState`
    // backfill it keeps existing saves on the clean "migrated" path instead of
    // falsely flagging "recovered" for a brand-new additive field. Defensive: a
    // structurally-corrupt `settings` is left untouched for normalize to repair
    // (never assign onto a non-object — that would throw and abort the load).
    this.migrations.set(3, (state) => {
      const settings = state.settings;
      if (
        settings !== null &&
        typeof settings === "object" &&
        !Array.isArray(settings) &&
        typeof (settings as Record<string, unknown>).uiScale !== "number"
      ) {
        (settings as Record<string, unknown>).uiScale = 1;
      }
      return state;
    });

    // v4 → v5 (S130, Story Bible §8): clean win/lose canon — the dead
    // PURGE/MERGE choice fields (`epochsCompleted`, `choicesMade`) are dropped,
    // and `endingsReached` maps the legacy "purge" value (the old
    // `determineEnding` wrote it for any wave-35 run) to the canon "purged".
    // Defensive like v3→v4: a structurally-corrupt `storyProgress` is left for
    // normalize to repair.
    this.migrations.set(4, (state) => {
      const sp = state.storyProgress;
      if (sp !== null && typeof sp === "object" && !Array.isArray(sp)) {
        const rec = sp as Record<string, unknown>;
        delete rec.epochsCompleted;
        delete rec.choicesMade;
        const endings = Array.isArray(rec.endingsReached)
          ? rec.endingsReached
          : [];
        rec.endingsReached = [
          ...new Set(
            endings
              .map((e) => (e === "purge" ? "purged" : e))
              .filter((e) => e === "purged" || e === "resident"),
          ),
        ];
      }
      return state;
    });

    // v5 → v6 (S146/BL-142): the prescreen text-size is now **L only** — the
    // S (0.85) and M (1.0) steps and their control were retired, and the whole
    // app runs at the Large scale. Normalize any persisted S/M up to L so an
    // existing player lands on the one shipped presentation. Defensive like
    // v3→v4: a structurally-corrupt `settings` is left for normalize to repair
    // (never assign onto a non-object — that would throw and abort the load).
    this.migrations.set(5, (state) => {
      const settings = state.settings;
      if (
        settings !== null &&
        typeof settings === "object" &&
        !Array.isArray(settings)
      ) {
        (settings as Record<string, unknown>).uiScale = L_UI_SCALE;
      }
      return state;
    });

    // v6 → v7 (D-352, spec `2026-threat-profile-model`): the dead
    // `settings.difficulty` ("normal" | "hard", never read by any system —
    // see D-352 for the ground-truth grep) is replaced by
    // `settings.threatLevel`. Both legacy values — and a missing one —
    // collapse onto the SAME target, `DEFAULT_THREAT_LEVEL` ("containment"):
    // "hard" never had a reader, so mapping it to a higher tier would invent
    // a player history that never existed. Only fills `threatLevel` if it is
    // not already a string, so an already-migrated or hand-set value is not
    // clobbered here (defense in depth — `normalizeMetaState`'s AC-13 clamp
    // is the one place that pins it to a *valid* ID either way). Defensive
    // like v3→v4/v4→v5/v5→v6: a structurally-corrupt `settings` is left for
    // normalize to repair (never assign onto a non-object — that would throw
    // and abort the load, AC-14).
    this.migrations.set(6, (state) => {
      const settings = state.settings;
      if (
        settings !== null &&
        typeof settings === "object" &&
        !Array.isArray(settings)
      ) {
        const rec = settings as Record<string, unknown>;
        if (typeof rec.threatLevel !== "string") {
          rec.threatLevel = DEFAULT_THREAT_LEVEL;
        }
        delete rec.difficulty;
      }
      return state;
    });

    // v7 → v8 (2026-08-16, `2026-threat-gameplay-contract`): `HighscoreEntry`
    // gains `threatLevel`, because the threat contract makes the five levels
    // mechanically different and a ranking that mixes them stops meaning
    // anything. Historical entries are backfilled with `containment` — NOT a
    // guess: until this round all five levels were mechanically identical
    // (proven by `tests/integration/sim-threat-level-neutrality.test.ts`), so
    // every score in an old save was in fact played at containment difficulty.
    // Defensive like every migration before it: a non-array `highscores` or a
    // non-object row is left for `normalizeMetaState` to repair rather than
    // written into (assigning onto a non-object would throw and abort the load).
    this.migrations.set(7, (state) => {
      const list = state.highscores;
      if (Array.isArray(list)) {
        for (const row of list) {
          if (row !== null && typeof row === "object" && !Array.isArray(row)) {
            const rec = row as Record<string, unknown>;
            if (typeof rec.threatLevel !== "string") {
              rec.threatLevel = DEFAULT_THREAT_LEVEL;
            }
          }
        }
      }
      return state;
    });
  }
}
